import { useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Pause, Play, Search } from 'lucide-react';

import PlaybackProgress from '@/components/PlaybackProgress';
import { RECO_SHARE_ROUTE } from '@/constants/routes';
import { useTranslation } from '@/utils/i18n';
import { useRecoDraft } from '@/stores/recoDraft';
import type { Track } from '@/types';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useDismissKeyboardOnDrag } from '@/hooks/useDismissKeyboardOnDrag';
import { useSearchTracks } from '@/hooks/queries/tracks';
import { getTrackImageUrl } from '@/utils/imageHelpers';
import { getPreviewUrl, useAudioPlayer } from '@/stores/audioPlayer';
import StreamingServiceSelector from '@/components/StreamingServiceSelector';
import FeedbackCard from '@/components/FeedbackCard';
import TabBarSpacer from '@/components/navigation/TabBarSpacer';

/**
 * "What to recommend" — the 🎵 tab target, and the search itself.
 *
 * share-screen-search — ONE screen: the field is real and focused on arrival
 * (the tap on 🎵 is the user's only gesture). Empty, it shows the streaming
 * selector and the feedback card; from the first letter, the search takes
 * their place. It used to be a fake field opening a separate `/search` screen,
 * whose focus — behind a navigation — never opened the iOS keyboard.
 *
 * Flow: 🎵 → /recommendation (search) → /reco-share → validate.
 */
export const Route = createFileRoute('/_tabs/recommendation')({
  component: RecommendationScreen,
});

function RecommendationScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { toggle, currentId, isPlaying } = useAudioPlayer();
  const setDraft = useRecoDraft((s) => s.setDraft);
  const inputRef = useRef<HTMLInputElement>(null);
  const { onTouchStart, onTouchMove, closeKeyboard } = useDismissKeyboardOnDrag(inputRef);

  // share-screen-search — the screen swaps on the RAW text (from the first
  // letter: the selector must not stay under the finger), the search runs on
  // the debounced one, from 2 characters as before.
  const typing = query.trim() !== '';
  const debounced = useDebouncedValue(query, 350);
  const { data, isFetching, isError, error } = useSearchTracks(debounced);
  const isNoNetwork = isError && !(error as { response?: unknown } | null)?.response;
  const results = data ?? [];
  const hasQuery = debounced.trim().length >= 2;
  const noResults = hasQuery && !isFetching && !isNoNetwork && results.length === 0;

  const pickTrack = (track: Track) => {
    setDraft(track, 'deezer');
    navigate({ to: RECO_SHARE_ROUTE });
  };

  return (
    <div
      className="tw:flex tw:h-full tw:flex-col tw:overflow-y-auto tw:px-5"
      data-testid="screen-recommendation"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
    >
      <h1 className="tw:py-5 tw:heading-2 tw:text-white">{t('recommendation.whatToRecommend')}</h1>

      {/* share-screen-search — the real field, focused on arrival: the tap on 🎵
          is the user's only gesture, the keyboard must already be up. */}
      <div className="tw:flex tw:h-[60px] tw:shrink-0 tw:items-center tw:gap-3 tw:rounded-[28px] tw:border tw:border-white/80 tw:bg-dark-1/70 tw:px-4">
        <Search size={18} className="tw:text-white/60" />
        <input
          ref={inputRef}
          type="text"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('search.placeholder')}
          data-testid="input-search"
          className="tw:h-full tw:flex-1 tw:bg-transparent tw:text-base tw:text-white tw:outline-none tw:placeholder:text-grey-4"
        />
      </div>

      {typing ? (
        <div className="tw:mt-5 tw:flex-1">
          {!hasQuery && <p className="tw:text-base tw:text-grey-2">{t('search.title')}</p>}
          {isNoNetwork && (
            <p className="tw:mt-2 tw:text-center tw:text-base tw:text-white/80">
              {t('search.noNetwork')}
            </p>
          )}
          {noResults && (
            <p className="tw:mt-2 tw:text-center tw:text-base tw:text-white/80">
              {t('search.noResults')}
            </p>
          )}
          {hasQuery && isFetching && results.length === 0 && (
            <div className="tw:mt-10 tw:flex tw:justify-center">
              <span className="tw:size-7 tw:animate-spin tw:rounded-full tw:border-2 tw:border-white/20 tw:border-t-blue-primary" />
            </div>
          )}

          <ul className="tw:flex tw:flex-col tw:gap-3">
            {results.map((track) => {
              const image = getTrackImageUrl(track);
              const preview = getPreviewUrl(track);
              const audioId = `search-${track.id}`;
              const playing = currentId === audioId && isPlaying;
              return (
                <li key={track.id}>
                  <button
                    type="button"
                    onClick={() => pickTrack(track)}
                    data-testid={`search-result-${track.id}`}
                    className="tw:flex tw:w-full tw:items-center tw:gap-3 tw:rounded-xl tw:bg-dark-1/70 tw:p-3 tw:text-left"
                  >
                    {/* J1 — listen straight from the results: tap the cover to play/pause. */}
                    {/* playback-progress-ring — the cover clips its overflow, so the outline
                        is its sibling, drawn 4 px around it. */}
                    <span className="tw:relative tw:size-[72px] tw:shrink-0">
                      <span
                        role="button"
                        aria-label={playing ? 'Pause' : 'Play'}
                        data-testid={`btn-search-play-${track.id}`}
                        onClick={(e) => {
                          if (!preview) return;
                          e.stopPropagation();
                          closeKeyboard();
                          toggle(preview, audioId, undefined, {
                            title: track.title,
                            artist: track.artists?.map((a) => a.name).join(', '),
                            artwork: image,
                          });
                        }}
                        className="tw:relative tw:flex tw:size-full tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-lg tw:bg-dark-primary tw:text-white/70"
                      >
                        {image && (
                          <img src={image} alt="" className="tw:size-full tw:object-cover" />
                        )}
                        {preview && (
                          <span className="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:bg-black/35 tw:text-white">
                            {playing ? (
                              <Pause size={20} fill="currentColor" />
                            ) : (
                              <Play size={20} fill="currentColor" />
                            )}
                          </span>
                        )}
                      </span>
                      <PlaybackProgress audioId={audioId} shape="rounded" size={72} radius={24} />
                    </span>
                    <span className="tw:flex tw:min-w-0 tw:flex-col">
                      <span className="tw:truncate tw:text-[15px] tw:font-semibold tw:text-white">
                        {track.title}
                      </span>
                      <span className="tw:truncate tw:caption tw:text-grey-3">
                        {track.artists?.map((a) => a.name).join(', ')}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <>
          {/* Favorite streaming service selector */}
          <StreamingServiceSelector className="tw:mt-6 tw:shrink-0 tw:rounded-2xl tw:bg-dark-1 tw:p-5" />

          {/* Report a bug (Sentry) or suggest an idea (Google Form) */}
          <FeedbackCard trigger="reco" />
        </>
      )}

      {/* Real bottom space before the tab bar: WebKit (iOS WebView) ignores the
          padding-bottom of a scrollable flex container, so pb-6 never showed. */}
      <div
        aria-hidden="true"
        className="tw:h-6 tw:shrink-0"
        data-testid="recommendation-bottom-spacer"
      />
      <TabBarSpacer />
    </div>
  );
}
